import {useState, useCallback, useEffect} from 'react';
import {
  useDeleteSummarizesJob,
  useGetSummarizesJobs,
  useNavigationSummarize,
  useRetrySummarizesJob,
} from '../../../../hooks/ai/summarized/useSummarizes';
import {
  BgNavigationTypes,
  SourceTypeCode,
  ToastType,
} from '../../../../shared/constants';
import {t} from 'i18next';
import {SummaryDataType} from './SummarizedDataGridType';
import {GridFilterModel, GridSortModel} from '@mui/x-data-grid';
import {
  IAISummarizeJobResponse,
  ISummarizationNavigationPayload,
} from '../../../../api/types/aiSummarization.type';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../../../redux/slice/docViewSlice';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../redux/slice/docListSlice';
import {RootState} from '../../../../redux/store';
import {formatUTCDateTime} from '../../../../utils/utils';
export const UseSummarizedGrid = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'dateSubmitted',
      sort: 'desc',
    },
  ]);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
  }, []);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [docListMasterId, setDocListMasterId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const {mutate: deleteJobId, isPending: isLoadingDelete} =
    useDeleteSummarizesJob();
  const {mutate: retrySummarizedJob, isPending: isLoadingRetry} =
    useRetrySummarizesJob();
  const {mutate: mutateSumarizeNavigation, isPending: navigationLoading} =
    useNavigationSummarize();
  const [selectedJobId, setSelectedJobId] = useState<
    SummaryDataType | undefined
  >();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType?.Success);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [summarizeJobData, setSummarizeJobData] =
    useState<IAISummarizeJobResponse>();
  const payload = {
    sortingColumn: sortModel[0]?.field ?? '',
    direction: sortModel[0]?.sort ?? '',
    displayLength: pageSize,
    pageNumber: pageNumber,
    columnFilter: filterModel.items?.map((data) => {
      return {
        columnName: data.field,
        columnValue: data.value,
      };
    }),
  };

  const {
    data: summarizeAIJobData,
    isLoading: summarizeDataLoading,
    refetch,
  } = useGetSummarizesJobs(payload);

  useEffect(() => {
    setSummarizeJobData(summarizeAIJobData);
  }, [summarizeAIJobData]);

  useEffect(() => {
    if (!isNavigating) return;
    const {navigationDestinationType: navType, notificationId} =
      lastEvent?.payload?.payload || {};

    if (lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_COMPLETE) {
      if (
        notificationId === Number(docListMasterId) &&
        (navType === SourceTypeCode.DOCVIEW ||
          navType === SourceTypeCode.DOCLIST)
      ) {
        setIsLoading(false);
        setIsNavigating(false);
        dispatch(storeDocumentListMasterId(String(docListMasterId)));
        dispatch(storeSourceTypeCode(SourceTypeCode.DOCUMENT));
        dispatch(setDocListId(Number(docListMasterId)));
        const backPath = `${window.location.pathname}${window.location.search}`;
        dispatch(
          setDocListBackNavigation({
            label: t('ai.summarize.header.title'),
            path: backPath,
          })
        );
        navigate('/Document/DocList');
      }
      return;
    }

    if (
      lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_ERROR &&
      Number(notificationId) === 0
    ) {
      setIsLoading(false);
      triggerToast(
        t('docExplorer.docDataGrid.navigationErrorToastText'),
        ToastType.Error
      );
      return;
    }
  }, [lastEvent, docListMasterId]);

  useEffect(() => {
    if (lastEvent?.payload.type === 'AISummaryNotification') {
      const isMatched = summarizeJobData?.jobDetailsDtos?.some(
        (item) => item?.jobId === lastEvent?.payload?.payload?.jobId
      );
      if (isMatched) {
        const summarizeUpdate: IAISummarizeJobResponse = {
          ...summarizeJobData,
          totalRecords: summarizeJobData?.totalRecords ?? 0,

          jobDetailsDtos:
            summarizeJobData?.jobDetailsDtos?.map((item) => {
              if (item?.jobId === lastEvent?.payload?.payload?.jobId) {
                const jobData = lastEvent?.payload?.payload;

                return {
                  ...item,
                  status: jobData?.jobStatus,
                  completedDocCount: jobData?.completedDocCount,
                  excludedDocCount: jobData?.errorDocCount,
                  dateCompleted:
                    jobData?.jobStatus === 'Completed'
                      ? formatUTCDateTime(jobData?.createdAt)
                      : '',
                };
              }

              return item;
            }) ?? [],
        };

        setSummarizeJobData(summarizeUpdate);
      }
    }
  }, [lastEvent]);

  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const handleDeletePress = useCallback(
    (selectedJobData?: SummaryDataType) => {
      setOpenDeleteModal(!openDeleteModal);
      if (selectedJobData?.jobId) {
        setSelectedJobId(selectedJobData);
      }
    },
    [openDeleteModal]
  );

  const onRetryData = useCallback((selectedJobData: SummaryDataType) => {
    retrySummarizedJob(selectedJobData?.jobId, {
      onSuccess: () => {
        refetch();
        triggerToast(t('ai.summarize.retry.success'), ToastType?.Success);
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
      },
    });
  }, []);
  const onDeleteData = useCallback(() => {
    deleteJobId(selectedJobId?.jobId, {
      onSuccess: () => {
        setOpenDeleteModal(false);
        refetch();
        triggerToast(t('ai.summarize.deleteModal.success'), ToastType.Success);
      },
      onError: (error: any) => {
        setOpenDeleteModal(false);
        const detail: string | undefined = error?.response?.data?.detail;
        const rpmxMatch = detail?.match(/RPMX ERROR\s+(\d+)/i);
        const rpmxCode = rpmxMatch ? rpmxMatch[1] : null;
        if (rpmxCode === '92001000005') {
          triggerToast(
            t('ai.summarize.deleteModal.inprogressDeleteError'),
            ToastType.Error
          );
        } else {
          triggerToast(error.message, ToastType.Error);
        }
      },
    });
  }, [selectedJobId]);

  const navigationDocs = (payload: ISummarizationNavigationPayload) => {
    mutateSumarizeNavigation(payload, {
      onSuccess: (data) => {
        setDocListMasterId(data?.doclistMasterId);
        setIsNavigating(true);
        setIsLoading(true);
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
      },
    });
  };

  return {
    onDeleteData,
    onRetryData,
    openDeleteModal,
    showToast,
    toastMessage,
    toastStatus,
    handleDeletePress,
    isLoadingDelete,
    isLoadingRetry,
    selectedJobId,
    setToastMessage,
    setShowToast,
    filterModel,
    handleSortModelChange,
    setFilterModel,
    sortModel,
    setPageSize,
    pageSize,
    setPageNumber,
    pageNumber,
    summarizeJobData,
    summarizeDataLoading,
    navigationDocs,
    navigationLoading,
    refetch,
    isLoading,
  };
};
